import type { SimpleGit } from 'simple-git';
import { revParseSafe } from './rev-parse-safe.js';

const DEFAULT_REMOTE_BRANCH_NAMES = ['main', 'master', 'trunk', 'develop'] as const;

function defaultRemoteBranchNameRefs(remote: string): string[] {
  return DEFAULT_REMOTE_BRANCH_NAMES.map((name) => `refs/remotes/${remote}/${name}`);
}

async function resolveRemoteHeadSymbolicRef(g: SimpleGit, remote: string): Promise<string | null> {
  const headSym = `refs/remotes/${remote}/HEAD`;
  try {
    const out = await g.raw(['symbolic-ref', '-q', '--verify', headSym]);
    const resolved = String(out).trim();
    return resolved.startsWith('refs/remotes/') ? resolved : null;
  } catch {
    return null;
  }
}

/**
 * Default integration branch on the remote (e.g. `refs/remotes/origin/main` from `origin/HEAD`),
 * used when this branch has no remote-tracking ref yet (never pushed).
 */
export async function resolveDefaultRemoteBranchRef(
  g: SimpleGit,
  remote: string,
): Promise<string | null> {
  const nameRefs = defaultRemoteBranchNameRefs(remote);
  const [symRef, ...nameShas] = await Promise.all([
    resolveRemoteHeadSymbolicRef(g, remote),
    ...nameRefs.map((ref) => revParseSafe(g, ref)),
  ]);
  if (symRef) return symRef;
  for (let i = 0; i < nameRefs.length; i++) {
    if (nameShas[i]) return nameRefs[i]!;
  }
  return null;
}

export async function resolveDefaultRemoteBranchSha(g: SimpleGit, remote: string): Promise<string | null> {
  const nameRefs = defaultRemoteBranchNameRefs(remote);
  const [symRef, ...nameShas] = await Promise.all([
    resolveRemoteHeadSymbolicRef(g, remote),
    ...nameRefs.map((ref) => revParseSafe(g, ref)),
  ]);
  if (symRef) return revParseSafe(g, symRef);
  for (let i = 0; i < nameRefs.length; i++) {
    if (nameShas[i]) return nameShas[i]!;
  }
  return null;
}
